// Typed fetch wrapper around the backend API. Shared by the 2D and VR apps.
import type { Page, Startpage, User, Widget } from "@/core/types";

const BASE = import.meta.env.VITE_API_BASE_URL ?? "";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: "include", // send the HttpOnly session cookie
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => resp.statusText);
    throw new ApiError(resp.status, text);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export const api = {
  // auth
  loginWithGoogle: (idToken: string) =>
    request<User>("/auth/google", { method: "POST", body: JSON.stringify({ id_token: idToken }) }),
  logout: () => request<{ ok: boolean }>("/auth/logout", { method: "POST" }),
  me: () => request<User>("/auth/me"),

  // startpages
  listMine: () => request<Startpage[]>("/startpages"),
  create: (body: Partial<Startpage>) =>
    request<Startpage>("/startpages", { method: "POST", body: JSON.stringify(body) }),
  get: (id: string) => request<Startpage>(`/startpages/${id}`),
  update: (id: string, body: Partial<Startpage>) =>
    request<Startpage>(`/startpages/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: string) => request<void>(`/startpages/${id}`, { method: "DELETE" }),
  fork: (id: string) => request<Startpage>(`/startpages/${id}/fork`, { method: "POST" }),

  // discovery
  publicFeed: (cursor?: string) =>
    request<Page<Startpage>>(`/discover/public${cursor ? `?cursor=${cursor}` : ""}`),
  byTag: (tag: string) => request<Page<Startpage>>(`/discover/tag/${encodeURIComponent(tag)}`),
  search: (q: string) => request<Page<Startpage>>(`/discover/search?q=${encodeURIComponent(q)}`),
  popularTags: () => request<string[]>("/discover/tags"),

  // widget data proxies
  weather: (lat: number, lon: number) =>
    request<WeatherNow>(`/widgets/weather?lat=${lat}&lon=${lon}`),
  news: (topic: string, lang = "en") =>
    request<NewsItem[]>(`/widgets/news?topic=${encodeURIComponent(topic)}&lang=${lang}`),
  rss: (url: string) => request<NewsItem[]>(`/widgets/rss?url=${encodeURIComponent(url)}`),
};

export interface WeatherNow {
  temperature: number;
  windspeed: number;
  weathercode: number;
  is_day: number;
  time: string;
}

export interface NewsItem {
  title: string;
  url: string;
  source: string;
  published: string;
}

export type { Widget };
